import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Loader from "../Loader/Loader";
import { API_URL } from "../../constants/urls";
import moment from "moment";
import "./Dashboard.css";

const DevicesList = ({ searchText }) => {
  const { user } = useSelector((state) => ({ ...state.auth }));
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  const [message, setMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const devicesPerPage = 20;
  const navigate = useNavigate();
  const userIds = user?.id;

  const deviceDetails = (deviceId, name) => {
    navigate("/device-channels", { state: { deviceId, name } });
  };

  function isOnline(updatedAt) {
    if (!updatedAt) return false;
    const updatedTime = new Date(updatedAt).getTime();
    return Date.now() - updatedTime <= 24 * 60 * 60 * 1000;
  }

  function getTimeAgo(updatedAt, maxUnits = 3) {
    if (!updatedAt) return "-";
    const now = moment();
    const then = moment(updatedAt);
    if (!then.isValid()) return "-";

    const temp = then.clone();
    const years = now.diff(temp, "years");
    temp.add(years, "years");

    const months = now.diff(temp, "months");
    temp.add(months, "months");

    const days = now.diff(temp, "days");
    temp.add(days, "days");

    const hours = now.diff(temp, "hours");
    temp.add(hours, "hours");

    const minutes = now.diff(temp, "minutes");

    const units = [
      { val: years, name: "year" },
      { val: months, name: "month" },
      { val: days, name: "day" },
      { val: hours, name: "hour" },
      { val: minutes, name: "min" },
    ];

    const parts = [];
    for (const u of units) {
      if (u.val && parts.length < maxUnits) {
        parts.push(`${u.val} ${u.name}${u.val > 1 ? "s" : ""}`);
      }
    }
    return parts.length > 0 ? `${parts.join(" ")} ago` : "just now";
  }

  useEffect(() => {
    let userId = (window.location.state && window.location.state.userId) || user?.id;
    if (!userId) {
      setMessage("User not found");
      setLoading(false);
      return;
    }

    const url = `${API_URL}/get-devices/${userId}`;

    const fetchData = async () => {
      try {
        const response = await fetch(url);
        const json = await response.json();
        if (!response.ok) {
          setMessage(json.message || "Failed to load devices");
        } else {
          setDevices(json.data || []);
        }
      } catch (error) {
        console.error("error", error);
        setMessage("Something went wrong while fetching devices");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userIds]);

  // Filter devices by ID
  const filteredDevices = devices.filter((d) => d.deviceId?.toString().toLowerCase().includes(searchText.toLowerCase()));

  const totalPages = Math.ceil(filteredDevices.length / devicesPerPage);
  const sortedDevices = [...filteredDevices].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  const indexOfLastDevice = currentPage * devicesPerPage;
  const indexOfFirstDevice = indexOfLastDevice - devicesPerPage;
  const currentDevices = sortedDevices.slice(indexOfFirstDevice, indexOfLastDevice);

  const getPageNumbers = () => {
    let startPage, endPage;
    if (totalPages <= 5) {
      startPage = 1;
      endPage = totalPages;
    } else if (currentPage <= 3) {
      startPage = 1;
      endPage = 5;
    } else if (currentPage + 2 >= totalPages) {
      startPage = totalPages - 4;
      endPage = totalPages;
    } else {
      startPage = currentPage - 2;
      endPage = currentPage + 2;
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handlePrevious = () => currentPage > 1 && setCurrentPage((prev) => prev - 1);
  const handleNext = () => currentPage < totalPages && setCurrentPage((prev) => prev + 1);

  if (loading) return <Loader />;

  return (
    <>
      <div className="row">
        {currentDevices && currentDevices.length > 0 ? (
          currentDevices.map((device, index) => (
            <div key={device.deviceId || index} className="mb-4 col-12 col-sm-6 col-md-4 col-lg-3" onClick={() => deviceDetails(device.deviceId, device.name)}>
              <div className="card p-2 pointer h-100">
                <div className="row g-0">
                  <div>
                    <span className={`online-status ms-1 ${isOnline(device.updatedAt) ? "bg-success" : "bg-warning"}`}></span>
                  </div>
                  <div>
                    <div className="card-body">
                      <p className="card-text m-0">
                        <b>ID:</b> {device.deviceId}
                      </p>
                      <p className="card-text m-0">
                        <b>Name:</b> {device.name || "-"}
                      </p>
                      <p className="card-text m-0">
                        <b>Last Value:</b> {device.lastData || "-"}
                      </p>
                      <p className="card-text m-0" title={device.updatedAt ? moment(device.updatedAt).format("DD-MM-YYYY hh:mm A") : ""}>
                        <b>Last Updated:</b> {getTimeAgo(device.updatedAt)}
                      </p>
                      <p className="card-text m-0">
                        <small className="text-muted">
                          <b>Channels:</b> {device.parameter}
                        </small>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center mt-5">
            <h1>{message || "No devices found"}</h1>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <nav>
          <ul className="pagination justify-content-center gap-2">
            <li className={`page-item non-active-page ${currentPage === 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={handlePrevious}>
                {"<"}
              </button>
            </li>
            {getPageNumbers().map((pageNumber) => (
              <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? "active-page" : "non-active-page"}`}>
                <button className="page-link" onClick={() => handlePageChange(pageNumber)}>
                  {pageNumber}
                </button>
              </li>
            ))}
            <li className={`page-item non-active-page ${currentPage === totalPages ? "disabled" : ""}`}>
              <button className="page-link" onClick={handleNext}>
                {">"}
              </button>
            </li>
          </ul>
        </nav>
      )}
    </>
  );
};

export default DevicesList;
